import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, Input, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import type { Editor, HugeRTE, RawEditorOptions } from 'hugerte';

/**
 * Componente wrapper do HugeRTE (Hugerte) para Angular 15.
 *
 * - Integra com Reactive Forms via ControlValueAccessor.
 * - Carrega a instância a partir de `window.hugerte` (script em `index.html`).
 *
 * @example
 * <app-hugerte-editor formControlName="htmlMessage" [config]="hugerteConfig"></app-hugerte-editor>
 */
@Component({
  selector: 'app-hugerte-editor',
  templateUrl: './hugerte-editor.component.html',
  styleUrls: ['./hugerte-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HugerteEditorComponent),
      multi: true
    }
  ]
})
export class HugerteEditorComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
  @ViewChild('textarea', { static: true }) textarea!: ElementRef<HTMLTextAreaElement>;

  /**
   * ID do elemento base (textarea). Se não informado, um id será gerado.
   */
  @Input() id = `hugerte-editor-${Math.random().toString(16).slice(2)}`;

  /**
   * Configuração do editor (compatível com a API do Hugerte).
   * Observação: não mutamos o objeto recebido; fazemos merge com defaults.
   */
  @Input() config: Partial<RawEditorOptions> | null = null;

  /**
   * Emite quando a instância do editor estiver pronta.
   */
  @Output() editorReady = new EventEmitter<Editor>();

  private editor: Editor | null = null;
  private pendingValue: string | null = null;
  private isDisabled = false;
  private isInitialized = false;

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(private zone: NgZone) {}

  ngAfterViewInit(): void {
    const hugerte = this.getHugeRte();
    if (!hugerte) {
      // Falha explícita para facilitar diagnóstico.
      throw new Error('Hugerte não encontrado em window.hugerte. Verifique se `hugerte/hugerte.min.js` foi carregado no `index.html`.');
    }

    /**
     * Importantíssimo: o Hugerte pode “puxar” o conteúdo inicial do `<textarea>`
     * em algum ponto do ciclo de inicialização. Se o `<textarea>` estiver vazio,
     * isso pode sobrescrever o valor vindo do backend e o editor aparece em branco.
     *
     * Portanto, sempre que já tivermos um valor pendente (writeValue antes do init),
     * aplicamos no `<textarea>` *antes* do `hugerte.init(...)`.
     */
    if (this.pendingValue !== null) {
      this.textarea.nativeElement.value = this.pendingValue;
    }

    const baseUrl = this.resolveHugerteBaseUrl();
    const userConfig = this.config ?? {};

    const initConfig: RawEditorOptions = {
      // Defaults importantes para resolver assets (plugins/skins/langs)
      base_url: (userConfig as RawEditorOptions).base_url ?? baseUrl,
      suffix: (userConfig as RawEditorOptions).suffix ?? '.min',
      target: this.textarea.nativeElement,
      ...userConfig,
      setup: editor => {
        this.editor = editor;
        this.isInitialized = false;

        // Propaga alterações para o FormControl.
        const emitValue = () => {
          const html = editor.getContent() ?? '';
          this.zone.run(() => this.onChange(html));
        };

        // Alguns eventos em editores do tipo Tiny/Hugerte são case-sensitive (ex.: SetContent).
        editor.on('change keyup undo redo SetContent setcontent', emitValue);
        editor.on('blur', () => this.zone.run(() => this.onTouched()));

        /**
         * Importante: não aplicar conteúdo inicial no `setup`.
         *
         * No Hugerte/Tiny, o editor pode sobrescrever o conteúdo durante o init
         * usando o valor do `<textarea>` (que aqui é sempre vazio).
         *
         * Por isso, aplicamos `pendingValue` somente quando o editor sinaliza `init`.
         */
        editor.on('init', () => {
          this.isInitialized = true;

          if (this.pendingValue !== null) {
            const initialValue = this.pendingValue;
            const applyInitialValue = () => {
              editor.setContent(initialValue);
              // Mantém o `<textarea>` sincronizado (o Hugerte pode ler dele em alguns fluxos).
              this.textarea.nativeElement.value = initialValue;
              // Garante sincronismo do FormControl mesmo que o evento SetContent não dispare.
              this.zone.run(() => this.onChange(initialValue));
            };

            applyInitialValue();
            // Segunda aplicação para evitar sobrescrita tardia durante o bootstrap do editor.
            setTimeout(applyInitialValue, 0);

            this.pendingValue = null;
          }

          this.applyDisabledState(editor, this.isDisabled);
          this.zone.run(() => this.editorReady.emit(editor));
        });

        // Se o usuário passou setup, chamamos também (sem sobrescrever o nosso).
        const userSetup = (userConfig as RawEditorOptions).setup;
        if (typeof userSetup === 'function') {
          userSetup(editor);
        }
      }
    };

    // A API init retorna Promise<Editor[]> no TinyMCE; no Hugerte é equivalente.
    void (hugerte as HugeRTE).init(initConfig);
  }

  ngOnDestroy(): void {
    if (this.editor) {
      this.editor.remove();
      this.editor = null;
    }
    this.isInitialized = false;
  }

  writeValue(value: string | null): void {
    const nextValue = value ?? '';

    // Mesmo antes do editor existir, mantemos o `<textarea>` com o valor atual
    // para evitar o Hugerte “resetar” o conteúdo lendo um textarea vazio.
    if (this.textarea?.nativeElement) {
      this.textarea.nativeElement.value = nextValue;
    }

    if (!this.editor || !this.isInitialized) {
      this.pendingValue = nextValue;
      return;
    }

    // Evita loop: só atualiza se o conteúdo realmente mudou.
    const current = this.editor.getContent() ?? '';
    if (current !== nextValue) {
      this.editor.setContent(nextValue);
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (this.editor) {
      this.applyDisabledState(this.editor, isDisabled);
    }
  }

  /**
   * Retorna o conteúdo do editor.
   *
   * @param options Opções do getContent (ex.: `{ format: 'text' }`).
   * Observação: este wrapper **não** suporta `format: 'tree'` (AST), e sempre normaliza para string.
   * @returns HTML (padrão) ou texto puro (se `format: 'text'`)
   * @example this.editor.getContent({ format: 'text' })
   */
  getContent(options?: Omit<Record<string, unknown>, 'format'> & { format?: 'raw' | 'text' | 'html' }): string {
    if (!this.editor) {
      return '';
    }

    // Evita resolver o overload de `format: 'tree'` (que retorna AstNode).
    const safeOptions = options as unknown as { format?: 'raw' | 'text' | 'html' } | undefined;
    const content = safeOptions ? this.editor.getContent(safeOptions) : this.editor.getContent();

    return typeof content === 'string' ? content : '';
  }

  private getHugeRte(): HugeRTE | null {
    return (window as unknown as { hugerte?: HugeRTE }).hugerte ?? null;
  }

  private resolveHugerteBaseUrl(): string {
    const baseHref = document.querySelector('base')?.href ?? `${window.location.origin}/`;
    // Queremos algo como: https://dominio/ + 'hugerte'
    const url = new URL('hugerte', baseHref);
    // TinyMCE/Hugerte costuma aceitar base_url sem barra final
    return url.toString().replace(/\/$/, '');
  }

  private applyDisabledState(editor: Editor, isDisabled: boolean): void {
    // Compatível com diferentes versões (TinyMCE 5/6 style APIs)
    const anyEditor = editor as any;
    if (isDisabled) {
      anyEditor.setMode?.('readonly');
      anyEditor.mode?.set?.('readonly');
    } else {
      anyEditor.setMode?.('design');
      anyEditor.mode?.set?.('design');
    }
  }
}
